import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE, PROJECT } from '../../constants/general.constants';

/** Webhook provisioning readiness for a connection, broken down by precondition (SCRUM-614). */
export interface BoxWebhookReadiness {
  ready: boolean;
  publicUrlConfigured: boolean;
  registryBacked: boolean;
  canManageWebhooks: boolean;
  signatureKeysSet: boolean;
  reasons: string[];
}

/** A Box webhook object as returned by the server proxy (Box `/2.0/webhooks`). */
export interface BoxWebhook {
  id: string;
  target?: { id: string; type: string };
  address?: string;
  triggers?: string[];
  /**
   * Human-readable path of the target folder ("All Files/Parent/Child"),
   * resolved server-side (EHQ-63). Absent if the path could not be resolved.
   */
  folderPath?: string;
}

export interface CreateBoxWebhookRequest {
  connectionId: number;
  routineId: number;
  folderId: string;
  triggers: string[];
}

/**
 * Client for the project-scoped Box webhook provisioning endpoints (SCRUM-614
 * Phase 4/5). All calls are project Admin server-side; readiness is project Read.
 */
@Injectable({ providedIn: 'root' })
export class BoxWebhookService {
  readonly #http = inject(HttpClient);

  #base(projectId: number): string {
    return `${API_BASE}/${PROJECT}/${projectId}/box/webhook`;
  }

  /** Report whether a connection can provision webhooks, by precondition. */
  getReadiness(projectId: number, connectionId: number): Observable<BoxWebhookReadiness> {
    const params = new HttpParams().set('connectionId', String(connectionId));
    return this.#http.get<BoxWebhookReadiness>(`${this.#base(projectId)}/readiness`, { params });
  }

  /**
   * List this project's Box webhooks provisioned through the connection. The
   * server scopes the result to the caller's project (EHQ-63) — it never
   * returns other projects' webhooks on a shared Box app.
   */
  list(projectId: number, connectionId: number): Observable<{ entries?: BoxWebhook[] }> {
    const params = new HttpParams().set('connectionId', String(connectionId));
    return this.#http.get<{ entries?: BoxWebhook[] }>(this.#base(projectId), { params });
  }

  /** Provision a Box webhook for a routine. */
  create(projectId: number, request: CreateBoxWebhookRequest): Observable<BoxWebhook> {
    return this.#http.post<BoxWebhook>(this.#base(projectId), request);
  }

  /** Delete a Box webhook by its Box id, using the connection's app credentials. */
  delete(projectId: number, boxWebhookId: string, connectionId: number): Observable<void> {
    const params = new HttpParams().set('connectionId', String(connectionId));
    return this.#http.delete<void>(`${this.#base(projectId)}/${boxWebhookId}`, { params });
  }
}
